/**
 * Сторінка «Нове» — редизайн v2.
 *
 * Що змінилось порівняно з попередньою версією (її збережено без змін у
 * updates.legacy.ts):
 *  - замість «один розділ = один рядок» — один запис на твір: діапазон розділів,
 *    розкривається в список за томами (див. updates-model.ts: реліз на 40+ розділів,
 *    необов'язкові томи, дробові номери, назви розділів);
 *  - немає ні кнопки «Оновити», ні жесту оновлення: перевірку джерел робить сервер
 *    за розкладом (src/scheduler.ts), а ця сторінка сама підхоплює свіже (опитування
 *    раз на хвилину, поки вкладка видима, і при поверненні на вкладку);
 *  - напис «Оновлено …» по центру під заголовком — живий: час «тікає», а коли
 *    з'являється нове, на кілька секунд показує «З'явилось нове: +N»;
 *  - часу й прогресу в списку немає.
 *
 * ВІДКАТ: UPDATES_V2_ENABLED = false (і перезбірка клієнта) повертає стару сторінку;
 * без перезбірки — відкрити /updates?design=old (запам'ятається в браузері),
 * /updates?design=new — повернути нову. Докладніше: docs/updates-v2.md.
 */
import { api, type HistoryItem, type UpdateItem } from '../api';
import { requireAuthOrRedirect } from '../auth';
import { renderNav } from '../nav';
import { coverEl, fmtDateGroup, fmtRelative, toast, pluginContentTypes, workHref } from '../utils';
import { warmSlugCache } from '../pretty-cache';
import { initUpdatesLegacy, readerUrl } from './updates.legacy';
import {
  buildWorks,
  summaryLines,
  unreadCount,
  unreadSignature,
  dayStartOf,
  type ContentKind,
  type Work,
} from './updates-model';

/** Головний перемикач відкату: false → завжди стара сторінка. */
export const UPDATES_V2_ENABLED = true;

const HIDE_READ_KEY = 'updates-hide-read';
const DESIGN_KEY = 'updates-design';
const POLL_MS = 60_000;


function useLegacy(): boolean {
  if (!UPDATES_V2_ENABLED) return true;
  try {
    const q = new URLSearchParams(location.search).get('design');
    if (q === 'old') localStorage.setItem(DESIGN_KEY, 'old');
    if (q === 'new') localStorage.removeItem(DESIGN_KEY);
    return localStorage.getItem(DESIGN_KEY) === 'old';
  } catch {
    return false;
  }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

/**
 * У розмітці (public/updates.html) кнопки «Перевірити оновлення» більше немає — інакше вона
 * миготіла б, доки JS не встиг її прибрати. Стара версія сторінки (відкат: ?design=old
 * або UPDATES_V2_ENABLED = false) без неї не працює, тож створюємо її тут — одразу, до
 * запуску старого коду й до будь-яких await.
 */
function ensureLegacyCheckButton() {
  if (document.getElementById('check-btn')) return;
  const box = document.querySelector('#updates-title-row .detail-actions');
  if (!box) return;
  const btn = el('button', 'iconbtn');
  btn.id = 'check-btn';
  btn.setAttribute('aria-label', 'Перевірити оновлення');
  btn.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 3v6h-6"/></svg>';
  box.appendChild(btn);
}

export async function initUpdates() {
  if (useLegacy()) {
    ensureLegacyCheckButton();
    return initUpdatesLegacy();
  }

  if (!(await requireAuthOrRedirect())) return;
  document.body.classList.add('updates-v2');
  renderNav('updates');
  const contentTypes = await pluginContentTypes();
  const kindOf = (pluginId: string): ContentKind => contentTypes[pluginId] ?? 'novel';

  const list = document.getElementById('list') as HTMLElement;
  const empty = document.getElementById('empty') as HTMLElement;
  const lastCheckEl = document.getElementById('last-check') as HTMLElement;
  const filterToggle = document.getElementById('filter-toggle') as HTMLButtonElement;
  // Оновлення суто автоматичні — кнопки перевірки в цій версії немає (її вже нема й у
  // public/updates.html; рядок нижче лише страхує на випадок застарілої розмітки з кешу).
  document.getElementById('check-btn')?.remove();
  empty.querySelector('div')!.textContent = 'Оновлень поки немає. Нові розділи зʼявляться тут автоматично.';
  filterToggle.setAttribute('aria-label', 'Показати лише непрочитані');

  let items: UpdateItem[] = [];
  let lastUpdateCheck: string | null = null;
  let historyByNovel = new Map<string, HistoryItem>();
  let hideRead = localStorage.getItem(HIDE_READ_KEY) === '1';
  let firstRender = true;
  let flashText: string | null = null;
  let flashTimer: number | undefined;
  let scan = { running: false, done: 0, total: 0 };
  let scanTimer: number | undefined;
  filterToggle.classList.toggle('active', hideRead);

  // ----- напис «Оновлено …» -----
  const status = el('div', 'u2-status');
  const dot = el('i', 'u2-dot');
  const statusText = el('span', 'u2-status-text');
  status.append(dot, statusText);
  lastCheckEl.replaceChildren(status);

  function setStatusText(text: string) {
    if (statusText.textContent === text) return;
    statusText.textContent = text;
    statusText.classList.remove('u2-swap');
    void statusText.offsetWidth; // перезапуск анімації
    statusText.classList.add('u2-swap');
  }
  function paintStatus() {
    status.classList.toggle('u2-found', flashText !== null);
    status.classList.toggle('u2-scan', scan.running && flashText === null);
    if (flashText !== null) return setStatusText(flashText);
    // Поки сервер перевіряє бібліотеку — показуємо її прогрес (твори), а не кількість джерел.
    if (scan.running) return setStatusText(`Перевіряю бібліотеку · ${scan.done} з ${scan.total}`);
    const base = lastUpdateCheck ? `Оновлено ${fmtRelative(lastUpdateCheck)}` : 'Бібліотеку ще не перевіряли';
    setStatusText(base + (hideRead ? ' · лише непрочитані' : ''));
  }
  function flash(text: string) {
    flashText = text;
    paintStatus();
    window.clearTimeout(flashTimer);
    flashTimer = window.setTimeout(() => {
      flashText = null;
      paintStatus();
    }, 4500);
  }

  // ----- побудова DOM -----
  const SCROLL_KEY = 'updates-scroll';
  const scroller = (): HTMLElement => (document.querySelector('.content') as HTMLElement) || document.documentElement;
  /** Перед виходом на твір/розділ запам'ятовуємо прокрутку — стрілка «назад» повертає на те саме місце. */
  function rememberScroll() {
    try {
      sessionStorage.setItem(SCROLL_KEY, JSON.stringify({ c: scroller().scrollTop, w: window.scrollY }));
    } catch {
      /* без sessionStorage просто повертаємось нагору */
    }
  }
  function restoreScroll() {
    let saved: { c: number; w: number } | null = null;
    try {
      const raw = sessionStorage.getItem(SCROLL_KEY);
      sessionStorage.removeItem(SCROLL_KEY);
      saved = raw ? JSON.parse(raw) : null;
    } catch {
      return;
    }
    if (!saved) return;
    const apply = () => {
      scroller().scrollTop = saved!.c;
      window.scrollTo(0, saved!.w);
    };
    apply();
    requestAnimationFrame(apply);
  }

  /**
   * Куди веде картка (тап і свайп вліво): перший непрочитаний із нових; якщо всі нові вже прочитані —
   * розділ, де зупинились (якщо він не дочитаний), інакше сторінка твору (там кнопка «Читати» веде
   * на наступний непрочитаний).
   */
  function openHref(w: Work): string {
    const first = w.chapters[0].item;
    if (w.firstUnread) return readerUrl(w.firstUnread.item, contentTypes, historyByNovel.get(w.novelId));
    const h = historyByNovel.get(w.novelId);
    if (h && w.kind !== 'anime' && h.progress < 100) {
      const asUpdate: UpdateItem = { ...h, checkedAt: '', read: false };
      return readerUrl(asUpdate, contentTypes, h);
    }
    return workHref(first.pluginId, first.novelPath, w.kind, '&from=updates');
  }

  function hideCard(w: Work, root: HTMLElement) {
    const gone = new Set(w.items.map((u) => `${u.novelId}\u0000${u.chapterPath}`));
    const prev = items;
    items = items.filter((u) => !gone.has(`${u.novelId}\u0000${u.chapterPath}`));
    root.style.height = `${root.offsetHeight}px`;
    void root.offsetHeight;
    root.classList.add('u2-gone');
    root.style.height = '0px';
    window.setTimeout(render, 320);
    api.dismissUpdates(w.items.map((u) => ({ novelId: u.novelId, chapterPath: u.chapterPath }))).catch((err) => {
      items = prev; // сервер не прийняв — повертаємо картку
      toast((err as Error).message);
      render();
    });
  }

  /** Свайп: вліво — відкрити (колір теми), вправо — приховати (жовтий). */
  function attachSwipe(w: Work, root: HTMLElement, head: HTMLElement) {
    let sx = 0, sy = 0, dx = 0, active = false, dragging = false, id = -1;
    let suppress = false;
    const width = () => head.offsetWidth || 360;
    const threshold = () => Math.max(80, width() * 0.3);
    const setX = (x: number) => {
      dx = x;
      head.style.transform = `translateX(${x}px)`;
      root.classList.toggle('sw-r', x > 0); // вправо — «Приховати»
      root.classList.toggle('sw-l', x < 0); // вліво — «Відкрити»
      root.style.setProperty('--sw', String(Math.min(1, Math.abs(x) / threshold())));
    };
    head.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      active = true; dragging = false; sx = e.clientX; sy = e.clientY; dx = 0; id = e.pointerId;
    });
    head.addEventListener('pointermove', (e) => {
      if (!active || e.pointerId !== id) return;
      const mx = e.clientX - sx;
      const my = e.clientY - sy;
      if (!dragging) {
        if (Math.abs(my) > 10 && Math.abs(my) > Math.abs(mx)) { active = false; return; } // це прокрутка
        if (Math.abs(mx) < 8 || Math.abs(mx) < Math.abs(my)) return;
        dragging = true;
        head.classList.add('drag');
        try { head.setPointerCapture(id); } catch { /* ок */ }
      }
      setX(Math.max(-width(), Math.min(width(), mx)));
    });
    const finish = (e: PointerEvent) => {
      if (!active || e.pointerId !== id) return;
      active = false;
      if (!dragging) return;
      dragging = false;
      suppress = true;
      window.setTimeout(() => (suppress = false), 350);
      head.classList.remove('drag');
      if (e.type === 'pointerup' && dx <= -threshold()) {
        rememberScroll();
        head.style.transform = `translateX(${-width()}px)`;
        location.href = openHref(w);
        window.setTimeout(() => setX(0), 600); // якщо перехід не відбувся (bfcache) — повертаємо картку на місце
      } else if (e.type === 'pointerup' && dx >= threshold()) {
        head.style.transform = `translateX(${width()}px)`;
        hideCard(w, root);
      } else {
        setX(0);
        root.classList.remove('sw-r', 'sw-l');
      }
    };
    head.addEventListener('pointerup', finish);
    head.addEventListener('pointercancel', finish);
    head.addEventListener('click', (e) => { if (suppress) { e.preventDefault(); e.stopPropagation(); } }, true);
    head.addEventListener('dragstart', (e) => e.preventDefault());
  }

  function buildItem(w: Work, n: number): HTMLElement {
    const root = el('div', `u2-item ${w.hasUnread ? 'fresh' : 'done'}`);
    root.style.setProperty('--n', String(Math.min(n, 12)));
    root.dataset.id = w.key;
    const first = w.chapters[0].item;

    // Підкладки, що з'являються при свайпі.
    const actOpen = el('div', 'u2-act open');
    actOpen.innerHTML = `<span>Відкрити</span><svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>`;
    const actHide = el('div', 'u2-act hide');
    actHide.innerHTML = `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24"/><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68"/><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61"/><path d="M2 2l20 20"/></svg><span>Приховати</span>`;

    const head = el('div', 'u2-head');
    // Обкладинка → сторінка твору (стрілка «назад» там веде сюди: from=updates).
    const cov = el('a', 'u2-cov');
    cov.href = workHref(first.pluginId, first.novelPath, w.kind, '&from=updates');
    cov.setAttribute('aria-label', `Сторінка твору: ${w.name}`);
    const cover = coverEl(w.name, w.cover, w.novelId);
    cover.classList.add('cover');
    cov.append(cover);
    cov.addEventListener('click', rememberScroll);

    // Решта картки → одразу розділ/серія (для аніме — сторінка твору).
    const main = el('a', 'u2-main');
    main.href = openHref(w);
    main.addEventListener('click', rememberScroll);
    const meta = el('div', 'u2-meta');
    meta.append(el('p', 'u2-title', w.name));
    const sum = el('div', 'u2-sum');
    summaryLines(w).forEach((l) => {
      const line = el('div', 'u2-sline');
      if (l.vol) line.append(el('span', 'u2-vol', l.vol));
      line.append(el('span', 'u2-range', l.range));
      sum.append(line);
    });
    meta.append(sum);
    main.append(meta);

    head.append(cov, main);
    root.append(actOpen, actHide, head);
    attachSwipe(w, root, head);
    return root;
  }

  function render() {
    const works = buildWorks(items, kindOf, { hideRead }).sort((a, b) => {
      const d = dayStartOf(b.latestAt) - dayStartOf(a.latestAt);
      if (d) return d;
      if (a.hasUnread !== b.hasUnread) return a.hasUnread ? -1 : 1;
      return b.latestAt.localeCompare(a.latestAt);
    });

    list.innerHTML = '';
    list.classList.toggle('u2-enter', firstRender);
    if (!works.length) {
      empty.style.display = 'flex';
      list.style.display = 'none';
      return;
    }
    empty.style.display = 'none';
    list.style.display = 'block';

    let group = '';
    works.forEach((w, i) => {
      const g = fmtDateGroup(w.latestAt);
      if (g !== group) {
        group = g;
        list.append(el('div', 'section-label', g));
      }
      list.append(buildItem(w, i));
    });
    firstRender = false;
  }

  function rebuildSlugHooks() {
    warmSlugCache(
      items.map((u) => ({ pluginId: u.pluginId, path: u.novelPath, title: u.novelName })),
      render,
    );
  }

  // ----- завантаження і автооновлення -----
  async function fetchState() {
    const [res, history] = await Promise.all([api.updates(), api.history().catch(() => [] as Awaited<ReturnType<typeof api.history>>)]);
    items = res.items;
    lastUpdateCheck = res.lastUpdateCheck;
    historyByNovel = new Map(history.map((h) => [h.novelId, h]));
  }

  /** Поки триває перевірка — раз на кілька секунд питаємо лише прогрес (дешево); коли закінчилась — підтягуємо новий список. */
  async function pollScan() {
    window.clearTimeout(scanTimer);
    if (document.hidden) return;
    let next = scan;
    try {
      next = await api.updatesStatus();
    } catch {
      return;
    }
    const wasRunning = scan.running;
    scan = next;
    paintStatus();
    if (scan.running) scanTimer = window.setTimeout(pollScan, 4000);
    else if (wasRunning) void poll();
  }

  async function poll() {
    if (document.hidden) return;
    const before = unreadSignature(items);
    const unreadBefore = unreadCount(items);
    try {
      await fetchState();
    } catch {
      return; // тиха помилка: наступне опитування спробує ще раз
    }
    if (unreadSignature(items) !== before) {
      render();
      const diff = unreadCount(items) - unreadBefore;
      if (diff > 0) flash(`З’явилось нове: +${diff}`);
    }
    paintStatus();
    if (!scan.running) void pollScan();
  }

  filterToggle.addEventListener('click', () => {
    hideRead = !hideRead;
    localStorage.setItem(HIDE_READ_KEY, hideRead ? '1' : '0');
    filterToggle.classList.toggle('active', hideRead);
    render();
    paintStatus();
  });

  try {
    await fetchState();
  } catch (err) {
    toast((err as Error).message);
  }
  paintStatus();
  render();
  rebuildSlugHooks();
  restoreScroll();
  void pollScan();

  window.setInterval(poll, POLL_MS);
  window.setInterval(paintStatus, 30_000); // «хвилину тому» → «2 хвилини тому» без запитів
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) void poll();
  });
}
